import { Navigate } from '@tanstack/react-router';
import { signInPath } from '@/shared/hms/host';
import { useAuth } from '@/shared/hooks/useAuth';
import { OrgsUnavailable } from '@/shared/components/OrgsUnavailable';
import { ShieldX } from 'lucide-react';

type OrgRole = 'owner' | 'admin' | 'consultant' | 'data_entry' | 'auditor' | 'viewer';

/**
 * Role hierarchy — higher index = more power.
 *
 * `auditor` sits with `viewer` at the read-only foot of the ladder, because
 * this ladder measures power over the BUSINESS. An auditor's own powers —
 * passing audit adjustments, issuing their report — are capabilities, not
 * rungs, and are gated by `usePermissions().can('audit_adjust' | 'audit_docs')`.
 * Putting the auditor higher would hand them every data-entry surface as well.
 */
const ROLE_HIERARCHY: Record<OrgRole, number> = {
    viewer: 0,
    auditor: 0,
    data_entry: 1,
    consultant: 2,
    admin: 3,
    owner: 4,
};

interface ProtectedRouteProps {
    /** Where a signed-out person signs in (hf-6: the hospital's own door). */
    signIn?: string;
    children: React.ReactNode;
    /** Minimum role required to access this route. Defaults to 'viewer' (any authenticated user). */
    minRole?: OrgRole;
}

/**
 * ProtectedRoute — wraps any page with auth + role checks.
 *
 * Usage:
 *   <ProtectedRoute>              → any logged-in user
 *   <ProtectedRoute minRole="staff">   → staff or higher
 *   <ProtectedRoute minRole="admin">   → admin or owner only
 */
export function ProtectedRoute({ children, minRole = 'viewer', signIn }: ProtectedRouteProps) {
    const { session, initialized, loading, orgs, orgsUnreadable, currentOrg } = useAuth();

    // Still loading auth state
    if (loading || !initialized) {
        return (
            <div className="flex items-center justify-center h-screen bg-stone-50 dark:bg-stone-900">
                <div className="w-8 h-8 border-4 border-stone-200 border-t-stone-600 rounded-full animate-spin" />
            </div>
        );
    }

    // Not logged in
    // hf-6: a hospital page, or any page on the hospital's address, signs in at the hospital's own door.
    if (!session) {
        return <Navigate to={signIn ?? signInPath()} />;
    }

    // CAREFUL. Empty `orgs` is TWO different facts, and only one is "new here".
    // If the membership read failed, saying "tell us about your business" to
    // somebody who has been keeping books in Karr for a year is a lie — and the
    // wizard's only button would create a SECOND, duplicate organisation. Ask
    // `orgsUnreadable` first; it is null only when we actually read the list.
    if (orgs.length === 0) {
        if (orgsUnreadable) return <OrgsUnavailable reason={orgsUnreadable} />;
        return <Navigate to="/onboarding" />;
    }

    // Role check
    const userRole = (currentOrg?.role as OrgRole) || 'viewer';
    const userLevel = ROLE_HIERARCHY[userRole] ?? 0;
    const requiredLevel = ROLE_HIERARCHY[minRole] ?? 0;

    if (userLevel < requiredLevel) {
        return <AccessDenied userRole={userRole} requiredRole={minRole} />;
    }

    return <>{children}</>;
}

/** Shown when user doesn't have the required role */
function AccessDenied({ userRole, requiredRole }: { userRole: string; requiredRole: string }) {
    return (
        <div className="flex flex-col items-center justify-center h-screen bg-stone-50 dark:bg-stone-900 gap-4">
            <div className="w-16 h-16 flex items-center justify-center">
                <ShieldX className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                Access Denied
            </h1>
            <p className="text-stone-500 dark:text-stone-400 text-center max-w-md">
                This page requires <span className="font-medium text-stone-700 dark:text-stone-300">{requiredRole}</span> access or higher.
                Your current role is <span className="font-medium text-stone-700 dark:text-stone-300">{userRole}</span>.
            </p>
            <p className="text-sm text-stone-400 dark:text-stone-500">
                Contact your organization admin to request access.
            </p>
            <a href="/" className="mt-4 text-sm font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400">
                ← Go to Dashboard
            </a>
        </div>
    );
}

export default ProtectedRoute;
